import Point from "@arcgis/core/geometry/Point";
import { geographicToWebMercator } from "@arcgis/core/geometry/support/webMercatorUtils";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import { WORLD_MAP_DEFAULT_MAP_MODE, WORLD_MAP_DEFAULT_MAP_STYLE, WORLD_MAP_MARKER_HEIGHT_PX, WORLD_MAP_MARKER_WIDTH_PX, getWorldMapGeoJSONLayerConfig } from "@components/world-map/world-map-constants";
import { LocalStorageUtils } from "@utils/local-storage-utils";
import { isMapMode, isMapStyle } from "@custom-types/world-map-type-guards";
import { sentryCaptureError } from "@utils/sentry-utils";

/** Local storage key under which the selected world map style is persisted. */
const MAP_STYLE_STORAGE_KEY = "map-style";

/** Local storage key under which the world map mode (2D/3D) is persisted. */
const MAP_MODE_STORAGE_KEY = "map-mode";

/** Number of horizontal sections used to decide popup side placement. */
const MARKER_POPUP_HORIZONTAL_SECTIONS = 3;

/** Minimum vertical space required above a marker so the popup can render on top. */
const MARKER_POPUP_MIN_TOP_SPACE_PX = 275;

/** Extra safe margin from the map top edge when placing marker popups. */
const MARKER_POPUP_TOP_SAFE_MARGIN_PX = 8;

/** Vertical gap between marker and popup card. */
const WORLD_MAP_MARKER_POPUP_GAP_PX = 2;

/**
 * Converts a world map entity to a GeoJSON Feature object representing the entity on the world map.
 * https://datatracker.ietf.org/doc/html/rfc7946#section-3.2
 * @param entity - World map entity
 * @returns GeoJSON feature object representing the entity
 */
function getWorldMapFeature(entity) {
  return {
    id: entity.id,
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [entity.position.lng, entity.position.lat]
    },
    properties: entity
  };
}

/**
 * Converts world map entities to a GeoJSON FeatureCollection object representing multiple entities on the world map.
 * https://datatracker.ietf.org/doc/html/rfc7946#section-3.3
 * @param entities - Array of entities
 * @returns GeoJSON FeatureCollection object representing the entities
 */
function getWorldMapFeatureCollection(entities) {
  return {
    type: "FeatureCollection",
    features: entities.map(getWorldMapFeature)
  };
}

/**
 * Creates a URL for the world map GeoJSON layer from the world map feature collection.
 * ArcGIS GeoJSONLayer requires a URL, not an object.
 * @param featureCollection - World map feature collection
 * @returns URL of the Blob containing the GeoJSON layer data
 */
function getWorldMapGeoJSONLayerUrl(featureCollection) {
  const blob = new Blob([JSON.stringify(featureCollection)], {
    type: "application/json"
  });
  return URL.createObjectURL(blob);
}

/**
 * Creates a instance of the GeoJSONLayer class.
 * @returns GeoJSONLayer instance
 */
function getWorldMapGeoJSONLayer(params, is3DEnabled = false) {
  return new GeoJSONLayer({
    ...params,
    ...getWorldMapGeoJSONLayerConfig(is3DEnabled)
  });
}

/** Return type for the getLayer function */

/**
 * @returns GeoJSONLayer instance created from the provided world map entities and fields,
 * along with the Blob URL that must be revoked when the layer is removed.
 */
export function getLayer({
  entities,
  fields,
  is3DEnabled = false
}) {
  const featureCollection = getWorldMapFeatureCollection(entities);
  const blobUrl = getWorldMapGeoJSONLayerUrl(featureCollection);
  const layer = getWorldMapGeoJSONLayer({
    url: blobUrl,
    fields
  }, is3DEnabled);
  return {
    layer,
    blobUrl
  };
}

/**
 * Chooses whether the popup should render above, below, or centered on marker Y.
 * If both top and bottom do not have enough vertical space, it falls back to center.
 */
export function getMarkerPopupPlacement(markerY, viewHeight) {
  const minTopSpaceWithMargin = MARKER_POPUP_MIN_TOP_SPACE_PX + MARKER_POPUP_TOP_SAFE_MARGIN_PX;
  const hasEnoughTopSpace = markerY >= minTopSpaceWithMargin;
  const hasEnoughBottomSpace = viewHeight - markerY >= MARKER_POPUP_MIN_TOP_SPACE_PX;
  if (!hasEnoughTopSpace && !hasEnoughBottomSpace) {
    return "center";
  }
  return hasEnoughTopSpace ? "top" : "bottom";
}

/**
 * Chooses whether the popup should render to the left, center, or right of a marker.
 * - Left third of the map: popup opens to the right of marker
 * - Middle third: popup stays centered on marker
 * - Right third: popup opens to the left of marker
 */
export function getMarkerPopupHorizontalPlacement(markerX, viewWidth) {
  const leftBoundary = viewWidth / MARKER_POPUP_HORIZONTAL_SECTIONS;
  const rightBoundary = viewWidth / MARKER_POPUP_HORIZONTAL_SECTIONS * 2;
  if (markerX <= leftBoundary) {
    return "right";
  }
  if (markerX >= rightBoundary) {
    return "left";
  }
  return "center";
}

/** Get horizontal translate value for marker popup placement */
export function getHorizontalTranslate(placement, verticalPlacement) {
  const sideOffset = verticalPlacement === "center" ? WORLD_MAP_MARKER_WIDTH_PX / 2 + WORLD_MAP_MARKER_POPUP_GAP_PX : 0;
  switch (placement) {
    case "right":
      return sideOffset > 0 ? `${sideOffset}px` : "0";
    case "left":
      return sideOffset > 0 ? `calc(-100% - ${sideOffset}px)` : "-100%";
    default:
      return "-50%";
  }
}

/** Get vertical translate value for marker popup placement */
export function getVerticalTranslate(placement) {
  const markerToPopupDistanceTopPx = WORLD_MAP_MARKER_HEIGHT_PX / 2 + WORLD_MAP_MARKER_POPUP_GAP_PX;
  if (placement === "top") {
    return `calc(-100% - ${markerToPopupDistanceTopPx}px)`;
  }
  if (placement === "center") {
    return "-50%";
  }
  return `${WORLD_MAP_MARKER_POPUP_GAP_PX}px`;
}

/**
 * Returns true if the given graphic represents a cluster (i.e. `cluster_count > 1`).
 * Used to filter out cluster graphics from hit-test results.
 */
export function isClusterGraphic(graphic) {
  const count = Number(graphic.attributes?.cluster_count);
  return Number.isFinite(count) && count > 1;
}

/**
 * Layers the pointer can hit: the 3D clustering layers when they exist,
 * otherwise the GeoJSON data layer of the 2D map.
 */
export function getHitTestLayers(dataLayer, clustering) {
  const clusteringLayers = [clustering?.clusterLayerRef.current, clustering?.markerLayerRef.current].filter(layer => Boolean(layer));
  if (clusteringLayers.length > 0) {
    return clusteringLayers;
  }
  return dataLayer ? [dataLayer] : [];
}

/**
 * Hit-tests the given layers (every layer when empty) at the screen point and
 * returns the first graphic matching the specified filter, or null if none is found.
 */
export async function hitTestGraphic(view, screenPoint, layers = [], isCluster = false) {
  const hitResult = layers.length > 0 ? await view.hitTest(screenPoint, {
    include: layers
  }) : await view.hitTest(screenPoint);
  const result = hitResult.results.find(r => r.type === "graphic" && isCluster === isClusterGraphic(r.graphic));
  return result?.type === "graphic" ? result.graphic : null;
}

/**
 * Converts world map entities to ArcGIS geometries in Web Mercator units (wkid: 3857).
 * @returns Array of ArcGIS geometries Web Mercator units (wkid: 3857).
 * @param entities Array of world map entities to convert.
 */
export function getWebMercatorGeometries(entities) {
  return entities.map(entity => {
    const geoPoint = new Point({
      longitude: entity.position.lng,
      latitude: entity.position.lat,
      spatialReference: {
        wkid: 4326
      }
    });
    return geographicToWebMercator(geoPoint);
  });
}

/** Earth radius in meters (WGS84 semi-major axis), used for the 3D horizon cull. */
const EARTH_RADIUS_METERS = 6378137;

/** Multiplier to convert an angle in degrees to radians (Math.PI / 180). */
const DEGREES_TO_RADIANS = 0.017453292519943295;

/** Converts a geographic position to earth-centered, earth-fixed (ECEF) cartesian coordinates (spherical earth). */
function geographicToEcef(longitude, latitude, altitude) {
  const radius = EARTH_RADIUS_METERS + altitude;
  const latitudeRad = latitude * DEGREES_TO_RADIANS;
  const longitudeRad = longitude * DEGREES_TO_RADIANS;
  const cosLatitude = Math.cos(latitudeRad);
  return [radius * cosLatitude * Math.cos(longitudeRad), radius * cosLatitude * Math.sin(longitudeRad), radius * Math.sin(latitudeRad)];
}

/**
 * Whether a geographic point is on the visible near hemisphere of the globe for
 * the current 3D camera — i.e. above the horizon and not hidden behind the
 * Earth's curvature. Always true in 2D, where there is no occlusion.
 *
 * A surface point P is visible from camera C when `dot(P, C) >= R²` (the plane
 * through the sphere's horizon of radius R). Without it, a point on the far side
 * of the globe would still project to on-screen pixels when zoomed out.
 */
export function isPointOnVisibleGlobeSide(view, point) {
  if (view.type !== "3d") {
    return true;
  }
  const camera = view.camera?.position;
  const cameraLongitude = camera?.longitude;
  const cameraLatitude = camera?.latitude;
  const pointLongitude = point.longitude;
  const pointLatitude = point.latitude;
  if (cameraLongitude === null || cameraLongitude === undefined || cameraLatitude === null || cameraLatitude === undefined || pointLongitude === null || pointLongitude === undefined || pointLatitude === null || pointLatitude === undefined) {
    return true;
  }
  const [cx, cy, cz] = geographicToEcef(cameraLongitude, cameraLatitude, camera?.z ?? 0);
  const [px, py, pz] = geographicToEcef(pointLongitude, pointLatitude, 0);
  return px * cx + py * cy + pz * cz >= EARTH_RADIUS_METERS * EARTH_RADIUS_METERS;
}

/**
 * Projects a map point to a rounded on-screen position, or null when it falls
 * outside the viewport. Does not test globe occlusion — combine with
 * {@link isPointOnVisibleGlobeSide} for a full 3D visibility check.
 */
export function projectPointToScreen(view, point) {
  const screenPoint = view.toScreen(point);
  if (!screenPoint || screenPoint.x < 0 || screenPoint.y < 0 || screenPoint.x > view.width || screenPoint.y > view.height) {
    return null;
  }
  return {
    x: Math.round(screenPoint.x),
    y: Math.round(screenPoint.y)
  };
}

/** Number of degrees in a full circle. */
const DEGREES_IN_CIRCLE = 360;

/** Number of degrees in a half circle. */
const DEGREES_IN_HALF_CIRCLE = 180;

/**
 * Angular radius (degrees) around the median project location used to define the
 * "main cluster" for the initial 3D view. Projects farther than this are treated
 * as outliers and excluded from the initial fit.
 */
const INITIAL_VIEW_CLUSTER_RADIUS_DEG = 60;

/** Returns the median of the given numbers. */
function getMedian(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
}

/**
 * Returns the signed shortest angular distance (degrees, range -180..180) from
 * longitude B to longitude A, wrapping across the antimeridian so points on
 * either side of ±180° are compared correctly.
 */
function getShortestLongitudeDelta(longitudeA, longitudeB) {
  return (longitudeA - longitudeB + DEGREES_IN_CIRCLE + DEGREES_IN_HALF_CIRCLE) % DEGREES_IN_CIRCLE - DEGREES_IN_HALF_CIRCLE;
}

/**
 * Returns the subset of entities that belong to the main cluster: those within
 * {@link INITIAL_VIEW_CLUSTER_RADIUS_DEG} of the median project location. Far-flung outliers are
 * dropped so the initial view frames where most projects are, instead of zooming out to the whole
 * globe (which would center on the ocean off Africa). If filtering would remove every entity, the
 * original list is returned unchanged.
 * @param entities Array of world map entities to filter.
 */
export function getMainClusterEntities(entities) {
  if (entities.length === 0) {
    return entities;
  }
  const medianLongitude = getMedian(entities.map(entity => entity.position.lng));
  const medianLatitude = getMedian(entities.map(entity => entity.position.lat));
  const clusteredEntities = entities.filter(entity => {
    const longitudeDelta = getShortestLongitudeDelta(entity.position.lng, medianLongitude);
    const latitudeDelta = entity.position.lat - medianLatitude;
    return Math.hypot(longitudeDelta, latitudeDelta) <= INITIAL_VIEW_CLUSTER_RADIUS_DEG;
  });
  return clusteredEntities.length > 0 ? clusteredEntities : entities;
}

/**
 * Reads the user's previously selected map style from local storage.
 *
 * @returns The stored map style or the default map style if none is found.
 */
export function getStoredMapStyle() {
  const storedMapStyle = LocalStorageUtils.getStringItem(MAP_STYLE_STORAGE_KEY);
  if (!isMapStyle(storedMapStyle)) {
    return WORLD_MAP_DEFAULT_MAP_STYLE;
  }
  return storedMapStyle;
}

/** Persists the user's selected map style to local storage. */
export function setStoredMapStyle(mapStyle) {
  LocalStorageUtils.setStringItem(MAP_STYLE_STORAGE_KEY, mapStyle);
}

/**
 * Reads the user's previously selected map mode (2D/3D) from local storage.
 *
 * @returns The stored map mode or the default map mode if none is found.
 */
export function getStoredMapMode() {
  const storedMapMode = LocalStorageUtils.getStringItem(MAP_MODE_STORAGE_KEY);
  if (!isMapMode(storedMapMode)) {
    return WORLD_MAP_DEFAULT_MAP_MODE;
  }
  return storedMapMode;
}

/** Persists the user's selected map mode (2D/3D) to local storage. */
export function setStoredMapMode(mapMode) {
  LocalStorageUtils.setStringItem(MAP_MODE_STORAGE_KEY, mapMode);
}

/**
 * Returns whether the given error is an `AbortError`. ArcGIS rejects promises
 * with an `AbortError` when an async operation is superseded or cancelled — for
 * example a `goTo` interrupted by a newer navigation, or a view destroyed while
 * still initializing (the user navigates away while the map is loading). These
 * are expected and should not be surfaced to the user or reported to Sentry.
 * @see https://developers.arcgis.com/javascript/latest/async-cancellation-with-abortcontroller
 */
export function isAbortError(error) {
  return typeof error === "object" && error !== null && "name" in error && error.name === "AbortError";
}

/**
 * Attaches a rejection handler to a fire-and-forget `view.goTo(...)` call so it
 * never surfaces as an unhandled promise rejection. The expected `AbortError`
 * (a newer navigation superseding this one) is ignored; any other failure is
 * reported to Sentry.
 * @param goToPromise - The promise returned by `view.goTo(...)`, or `undefined`
 * when the view is not available.
 */
export function runGoTo(goToPromise) {
  void goToPromise?.catch(error => {
    if (!isAbortError(error)) {
      sentryCaptureError({
        error,
        title: "World map navigation failed."
      });
    }
  });
}

/** Earth's mean radius in kilometers, used for great-circle distance calculations. */
const EARTH_RADIUS_KM = 6371;

/** Converts an angle in degrees to radians. */
function degreesToRadians(degrees) {
  return degrees * Math.PI / DEGREES_IN_HALF_CIRCLE;
}

/**
 * Great-circle (haversine) distance in kilometers between two longitude/latitude
 * points on the globe. Used to decide whether a fly-to animation is long enough
 * to warrant capping its duration.
 */
export function getGreatCircleDistanceKm(from, to) {
  const dLat = degreesToRadians(to.lat - from.lat);
  const dLong = degreesToRadians(to.lng - from.lng);
  const fromLatRad = degreesToRadians(from.lat);
  const toLatRad = degreesToRadians(to.lat);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(fromLatRad) * Math.cos(toLatRad) * Math.sin(dLong / 2) ** 2;

  // Clamp to 1 before `asin`: floating-point rounding can push `a` slightly above
  // 1 for near-antipodal points, which would make `Math.sqrt`/`Math.asin` return NaN.
  return EARTH_RADIUS_KM * 2 * Math.asin(Math.sqrt(Math.min(a, 1)));
}